import * as React from 'react';
import classnames from 'classnames';
import { Eraser, RotateCcw, Undo2 } from 'lucide-react';
import { InputError, renderIcon, Tooltip } from '@ixicare/ui';
import { t } from '~i18n';
import { Field, FieldValue, same } from '../schema';
import { beneathLayers } from '../utils/layerDetail';
import { Print, printer, valuePrintable } from '../utils/printValue';
import styles from '../styles.module.css';

/** A layer a value can come from, narrowest first in `LAYER_ORDER`. */
export type ConfigLayer = 'device' | 'owner' | 'site' | 'default';

/** Narrowest first. A layer earlier in this list displaces every layer after it. */
export const LAYER_ORDER: ConfigLayer[] = ['device', 'owner', 'site', 'default'];

/**
 * A layer that can sit beneath a form's own.
 *
 * Never the device's, which is the narrowest there is, and never the defaults, which arrive with
 * the form definition rather than as values. Narrowing it this way is what lets the labels below
 * be exhaustive instead of falling back on a key that would misname the layer.
 */
export type BeneathLayer = Exclude<ConfigLayer, 'device' | 'default'>;

// How each layer names itself in a stack. Keyed off the layer rather than written per stack, so a
// new layer is one entry here and one in `LAYER_ORDER`.
const LAYER_LABEL_KEY: Record<ConfigLayer, string> = {
  device: 'device-config.source.override',
  owner: 'device-config.source.owner',
  site: 'device-config.source.site',
  default: 'device-config.source.default',
};

export interface DifferingKeyDetail {
  key: string;
  // What the key reads as elsewhere: the site's value, or each device's where several are being
  // compared. Empty when there is nothing to add beside the label -- a field already showing the
  // site's value, or an override of a key the site never set.
  values: { mac?: string; value: FieldValue }[];
  // The value's relationship to the layer this form writes. `own` is stored in it; `own-cleared`
  // and `own-will-set` are staged in either direction and have not reached the service;
  // `inherited` is a layer below, which `inheritedFrom` names. Absent on the bulk form, which
  // knows only that the devices diverge from the site.
  // `drifted` is the odd one: the device still runs a value the site has since stopped setting,
  // because sending the site layer to the devices is a separate and optional step.
  source?: 'inherited' | 'own' | 'own-cleared' | 'own-will-set' | 'drifted';
  // Which layer the inherited value came from. Only set alongside `inherited`.
  inheritedFrom?: BeneathLayer;
  // Which layer `own` means. The device form and the site form say the same thing one layer apart,
  // so the relationship above is shared and this is what tells them apart.
  layer?: ConfigLayer;
  // On every `own` source, keyed by the layer each value belongs to. A layer is absent where the
  // form cannot read one, or where it holds nothing -- that absence is what makes a field say it
  // displaces the default rather than a site value.
  override?: Partial<Record<ConfigLayer, FieldValue>>;
  // The layers were derived rather than read off one parameter. A synthetic control's value belongs
  // to all of its parameters at once, so its detail names the field instead of a key.
  derived?: boolean;
}

const UNIT_LABEL: Record<string, string> = {
  seconds: '(s)',
  milliseconds: '(ms)',
  meters: '(m)',
  percent: '(%)',
  milli_g: '(milli-g)',
  meters_per_second_squared: '(m/s²)',
  g: '(g)',
  bits: '(bits)',
  hertz: '(Hz)',
};

export const formatUnit = (unit?: string) => {
  if (!unit) return '';
  return UNIT_LABEL[unit] ?? `(${unit})`;
};

export const labelWithUnit = (title: string, unit?: string): string => {
  const label = t(title);
  const unitLabel = formatUnit(unit);
  return unitLabel ? `${label} ${unitLabel}` : label;
};

interface Props {
  field: Field;
  error?: string;
  changed?: boolean;
  bulk?: boolean;
  differing?: boolean;
  differingDetail?: DifferingKeyDetail[];
  inheritedDetail?: DifferingKeyDetail[];
  // Read an unmarked field's value as inherited rather than as this form's own.
  dimInherited?: boolean;
  // Stage this field into or out of the layer the form writes. Absent where the form cannot write
  // one, or may not: a read-only form passes nothing and shows markers alone.
  onToggleStored?: () => void;
  /** Off for a form that edits one stored thing rather than a layer of several. Default on. */
  showFlags?: boolean;
  children: React.ReactNode;
}

const inheritedSummary = (detail: DifferingKeyDetail[], print: Print): string =>
  detail
    .filter((d) => d.values.length > 0)
    .map((d) => {
      const value = print(d.values[0]?.value);
      return detail.length > 1 ? `${d.key}: ${value}` : value;
    })
    .join(', ');

// A layer holds nothing worth naming when it has no value, or an empty one. The empty case is real:
// the wifi credentials default to '', so without this the flag trails a `: ` with nothing after it.
const nameable = (value: FieldValue): boolean => value !== undefined && value !== null && value !== '';

const displacedSummary = (detail: DifferingKeyDetail[], layer: ConfigLayer, print: Print): string =>
  detail
    .filter((d) => nameable(d.override?.[layer]))
    .map((d) => {
      const value = print(d.override![layer]);
      return detail.length > 1 ? `${d.key}: ${value}` : value;
    })
    .join(', ');

const differingTooltip = (detail: DifferingKeyDetail[], print: Print): React.ReactNode => (
  <div className={styles.tooltipDiffering}>
    {detail.map((d) => (
      <div key={d.key} className={styles.tooltipDifferingGroup}>
        {detail.length > 1 && <div className={styles.tooltipDifferingKey}>{d.key}</div>}
        {d.values.map((v) => (
          <div key={v.mac ?? d.key} className={styles.tooltipDifferingRow}>
            {v.mac && <span className={styles.tooltipDifferingMac}>{v.mac}</span>}
            <span>{print(v.value)}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const layerRow = (label: string, value: string, inEffect?: boolean): React.ReactNode => (
  <div className={classnames(styles.tooltipLayerRow, inEffect && styles.tooltipLayerActive)}>
    <span className={styles.tooltipLayerName}>
      <span className={styles.tooltipLayerMarker} aria-hidden>
        {inEffect ? '▸' : ''}
      </span>
      {label}
    </span>
    <span>{value}</span>
  </div>
);

type LayerRowSpec = {
  labelKey: string;
  pick: (o: NonNullable<DifferingKeyDetail['override']>) => FieldValue;
  inEffect?: boolean;
};

/**
 * The stack a field shows: the form's own layer and everything beneath it.
 *
 * One function rather than a table per case, so adding a layer is an entry in `LAYER_ORDER` and a
 * label key -- not a fourth table to keep in step with three others.
 *
 * `topLabelKey` renames the first row only. A drifted value uses it to say the device is *running*
 * the value rather than *overriding* with it, which would state the very thing the warning denies.
 */
const layerRows = ({
  from,
  inEffect,
  topLabelKey,
}: {
  from: ConfigLayer;
  inEffect?: ConfigLayer;
  topLabelKey?: string;
}): LayerRowSpec[] =>
  LAYER_ORDER.slice(LAYER_ORDER.indexOf(from)).map((layer, index) => ({
    labelKey: index === 0 && topLabelKey ? topLabelKey : LAYER_LABEL_KEY[layer],
    pick: (o) => o[layer],
    inEffect: layer === inEffect,
  }));

const layerStackTooltip = (detail: DifferingKeyDetail[], print: Print, rows: LayerRowSpec[]): React.ReactNode => (
  <div className={styles.tooltipDiffering}>
    {detail.map((d) => (
      <div key={d.key} className={styles.tooltipDifferingGroup}>
        {detail.length > 1 && <div className={styles.tooltipDifferingKey}>{d.key}</div>}
        {rows.map((row) => (
          <React.Fragment key={row.labelKey}>
            {layerRow(t(row.labelKey), print(d.override ? row.pick(d.override) : undefined), row.inEffect)}
          </React.Fragment>
        ))}
      </div>
    ))}
  </div>
);

// Which hue speaks for each layer. Only the layers a form can colour today; a layer with no entry
// renders unmarked rather than borrowing another layer's colour.
const LAYER_DOT_CLASS: Partial<Record<ConfigLayer, string>> = {
  device: styles.deviceOverride,
  owner: styles.ownerOverride,
  site: styles.siteConfigOverride,
};
const LAYER_FLAG_CLASS: Partial<Record<ConfigLayer, string>> = {
  device: styles.flagOverride,
  owner: styles.flagOwner,
  site: styles.flagSite,
};
const LAYER_FLAG_BOTH_CLASS: Partial<Record<ConfigLayer, string>> = {
  device: styles.flagBothOverride,
  owner: styles.flagBothOwner,
  site: styles.flagBoth,
};

// What a field says when its own value displaces one from the layer named, and what it says when
// it holds nothing of its own and takes that layer's value instead.
const LAYER_OVERRIDES_KEY: Record<BeneathLayer, string> = {
  owner: 'device-config.source.overrides-owner',
  site: 'device-config.source.overrides-site',
};
const LAYER_INHERITED_KEY: Record<BeneathLayer, string> = {
  owner: 'device-config.common.owner-setting',
  site: 'device-config.common.site-setting',
};

// Space kept between a wrapped label and the flag beside it.
const FLAGS_GAP_PX = 8;

const FieldWrapper: React.FC<Props> = ({
  field,
  error,
  changed,
  bulk,
  differing,
  differingDetail,
  inheritedDetail,
  dimInherited,
  onToggleStored,
  showFlags = true,
  children,
}) => {
  const print = printer(field);

  // The flags sit beside the label, out of the layout, so the label is told how much room they take:
  // it wraps short of their width and grows to their height (see FieldLabel). Measured on every
  // render, since every render can change which flags show. The flags' size never depends on the
  // label's, so setting it cannot loop.
  const fieldRef = React.useRef<HTMLDivElement>(null);
  const flagsRef = React.useRef<HTMLSpanElement>(null);
  React.useLayoutEffect(() => {
    const flags = flagsRef.current;
    const field = fieldRef.current;
    if (!flags || !field) return;
    const { width, height } = flags.getBoundingClientRect();
    // Height from the field's top, which includes the offset centring the flags on the first line.
    const bottom = width ? flags.offsetTop + height : 0;
    field.style.setProperty('--field-flags-width', width ? `${Math.ceil(width) + FLAGS_GAP_PX}px` : '0px');
    field.style.setProperty('--field-flags-height', `${Math.ceil(bottom)}px`);
  });
  const inherits = Boolean(inheritedDetail?.length);
  const isChanged = Boolean(field.highlight || changed);
  const source = inheritedDetail?.[0]?.source;
  const isCleared = source === 'own-cleared';
  const staged = isCleared || source === 'own-will-set';
  // Stored in the layer this form writes. A cleared field is still in it until the save lands, so
  // it still has a stack to show.
  const isStored = source === 'own' || isCleared;
  // Which layer this form writes. Drives the label and how deep the stack goes.
  const ownLayer = inheritedDetail?.[0]?.layer;
  const isDeviceLayer = ownLayer === 'device';
  const isDrifted = source === 'drifted';
  const ownsLayer = isStored || staged || isDrifted;
  // Inherited from a layer below, on a form that can read one: nothing of its own, but there is
  // still a stack worth showing -- what it runs, and what it would fall back to.
  const isInherited = source === 'inherited';
  // Which layer the flag's colour speaks for. Not the same question as `ownLayer`: an inherited
  // field is coloured for the layer the value came from, which is what the flag says out loud.
  const hueLayer: ConfigLayer = (isInherited ? inheritedDetail?.[0]?.inheritedFrom : ownLayer) ?? 'site';
  // A value with no printable form is named but never shown, in the flag as much as in the stack.
  // The marker stays either way, which is the part that says where the value came from.
  const printable = valuePrintable(field);
  // A synthetic control's value belongs to all of its parameters at once, so its layers have to be
  // derived; where the schema did not state their defaults they cannot be, and every layer but the
  // top would read as a dash.
  const derivable = !field.synthetic || Boolean(inheritedDetail?.[0]?.derived);
  const hasStack = (ownsLayer || isInherited) && printable && derivable;
  // Every stack starts at the layer the form writes, inherited ones included: what matters there
  // is that the form's own layer holds nothing, which the stack shows as a dash on its top row.
  const stackFrom: ConfigLayer = ownLayer ?? 'site';
  const rows = isDrifted
    ? layerRows({ from: stackFrom, inEffect: stackFrom, topLabelKey: 'device-config.source.running' })
    : isInherited
      ? layerRows({ from: stackFrom, inEffect: inheritedDetail?.[0]?.inheritedFrom })
      : layerRows({ from: stackFrom, inEffect: stackFrom });

  // A staged field's stack names no layer as in effect: saving will move it, so none of them is
  // what the device runs once it lands.
  const stackRows = staged ? rows.map((row) => ({ ...row, inEffect: false })) : rows;

  // The nearest layer beneath this form's own that holds something worth naming. That is the one
  // an edit here displaces, so it is the one the flag names -- a device value over a resident's
  // says so rather than naming the site two layers down. Either can turn out to hold nothing
  // printable, and then the label stands alone: the wifi credentials, whose default is empty.
  const displacedLayer = ownLayer
    ? beneathLayers(ownLayer).find((layer) => inheritedDetail?.some((d) => nameable(d.override?.[layer])))
    : undefined;
  const displacedBeneath = displacedLayer ? displacedSummary(inheritedDetail!, displacedLayer, print) : '';
  const displacedDefault = isStored && !displacedBeneath ? displacedSummary(inheritedDetail!, 'default', print) : '';
  const pinnedToDefault =
    source === 'own' &&
    ownLayer !== undefined &&
    inheritedDetail!.every((d) => d.override?.default !== undefined && same(d.override[ownLayer], d.override.default));

  // A staged field names no value: it says only that saving will move it, and the value it is
  // moving to is already in the control. Pinned names none either: the value it would name is the
  // one already in the control.
  const displaced =
    staged || !printable || (pinnedToDefault && !displacedBeneath) ? '' : displacedBeneath || displacedDefault;
  const inheritedValues =
    inherits && !ownsLayer && source !== 'inherited' ? inheritedSummary(inheritedDetail!, print) : '';
  // A field names itself by what it displaces: the nearest layer beneath holding a value, else the
  // default. A value pinned to the default displaces nothing different, so `overrides-default`
  // gives way to saying it is pinned; displacing a real layer is still worth naming, and then the
  // pinned flag stands on its own line.
  //
  // The tail differs by layer and always has: the device form says it overrides the default even
  // where that default is not printable, where the site form falls back to saying only that the
  // site holds this one at all.
  const ownLabel = displacedLayer
    ? LAYER_OVERRIDES_KEY[displacedLayer]
    : pinnedToDefault
      ? 'device-config.source.pinned-default'
      : isDeviceLayer || displacedDefault
        ? 'device-config.source.overrides-default'
        : 'device-config.source.site-set';

  // Staged either way reads as a pending change and nothing more: the control that staged it said
  // what it was going to do, and the form around it says which layer is being edited.
  const sourceLabel = isDrifted
    ? 'device-config.source.drifted'
    : staged
      ? 'device-config.common.changed'
      : isStored
        ? ownLabel
        : LAYER_INHERITED_KEY[inheritedDetail?.[0]?.inheritedFrom ?? 'site'];

  const cls = classnames(
    styles.field,
    field.span === 2 && styles.span2,
    field.span === 3 && styles.span3,
    field.span === 4 && styles.span4,
    field.enabled === false && styles.disabled,
    isDrifted && !isChanged && styles.flagDrifted,
    !isDrifted && staged && styles.flagChanged,
    !isDrifted && !staged && isChanged && !inherits && styles.flagChanged,
    !isDrifted && !staged && inherits && !isChanged && LAYER_FLAG_CLASS[hueLayer],
    !isDrifted && !staged && inherits && isChanged && LAYER_FLAG_BOTH_CLASS[hueLayer],
    // Nothing marks this field and nothing has been typed into it, so what it shows is inherited
    // rather than this form's own.
    dimInherited && !inherits && !isChanged && styles.flagInherited,
    bulk && differing && styles.flagDiffering
  );

  const inheritedFlag = (
    <span className={isDrifted ? styles.driftedDot : staged ? styles.changedDot : LAYER_DOT_CLASS[hueLayer]}>
      ● {t(sourceLabel)}
      {displaced && `: ${displaced}`}
      {inheritedValues && `: ${inheritedValues}`}
    </span>
  );

  // Only ever beside a marker, so the flag it sits next to is what explains it: drop a stored
  // field out of the layer, or undo either staging. A field the layer does not hold carries
  // nothing -- editing it is what asks it to join, so there is no affordance left to discover.
  const actionKey = isDrifted
    ? 'device-config.actions.use-inherited'
    : staged
      ? 'device-config.actions.unstage'
      : 'device-config.actions.clear-field';
  const toggle = onToggleStored && (isStored || staged || isDrifted) && (
    <button
      type="button"
      className={styles.flagButton}
      onClick={onToggleStored}
      aria-label={t(actionKey)}
      title={t(actionKey)}
    >
      {renderIcon(undefined, isDrifted ? RotateCcw : staged ? Undo2 : Eraser)}
    </button>
  );

  // A form with no layer beneath it and nothing to stage has nothing to flag: the resident
  // settings page stores one thing for the person, so a dot saying a value differs from what was
  // loaded would be the only thing in this row, and it tells a carer nothing they can act on.
  if (!showFlags) {
    return (
      <div ref={fieldRef} className={cls} id={field.id}>
        {children}
        <InputError error={error} />
      </div>
    );
  }

  return (
    <div ref={fieldRef} className={cls} id={field.id}>
      <span ref={flagsRef} className={styles.fieldFlags}>
        <span className={styles.flagRow}>
          {inherits &&
            // A flag naming the form's own layer carries the stack on hover, so it has to opt back
            // out of the flag overlay's pointer-events:none the same way the differing flag does.
            (hasStack ? (
              <span className={styles.differingTrigger}>
                <Tooltip
                  title={t('device-config.source.title')}
                  content={layerStackTooltip(inheritedDetail!, print, stackRows)}
                  overrideWidth={260}
                  hoverDelay={400}
                >
                  {inheritedFlag}
                </Tooltip>
              </span>
            ) : (
              inheritedFlag
            ))}
          {toggle}
        </span>
        {pinnedToDefault && sourceLabel !== 'device-config.source.pinned-default' && (
          <span className={LAYER_DOT_CLASS[hueLayer]}>● {t('device-config.source.pinned-default')}</span>
        )}
        {/* Staged already says a change is pending, and after a clear the value almost always
            differs from initial, so the generic dot would only repeat it. */}
        {(field.highlight || changed) && !staged && (
          <span className={styles.changedDot}>● {t('device-config.common.changed')}</span>
        )}
        {bulk &&
          (differing ? (
            differingDetail && differingDetail.length ? (
              <span className={styles.differingTrigger}>
                <Tooltip
                  title={t('device-config.bulk.differingTitle')}
                  content={differingTooltip(differingDetail, print)}
                  overrideWidth={260}
                  hoverDelay={400}
                >
                  <span className={styles.differingDot}>● {t('device-config.bulk.differs')}</span>
                </Tooltip>
              </span>
            ) : (
              <span className={styles.differingDot}>● {t('device-config.bulk.differs')}</span>
            )
          ) : (
            <span className={styles.commonDot}>● {t('device-config.bulk.common')}</span>
          ))}
      </span>
      {children}
      <InputError error={error} />
    </div>
  );
};

export default FieldWrapper;
